import { buildSheetConfig } from "../domain/sheetSize";
import { isValidRestStockItem, restStockForThickness } from "../domain/restStock";

const MIN_PANEL_SIZE = 40;

class BinaryTreePacker {
  constructor(length, width) {
    this.root = { x: 0, y: 0, length, width, used: false };
  }

  clone() {
    const cloned = new BinaryTreePacker(0, 0);
    cloned.root = JSON.parse(JSON.stringify(this.root));
    return cloned;
  }

  fitOne(length, width) {
    const node = this.findNode(this.root, length, width);
    return node ? this.splitNode(node, length, width) : null;
  }

  findNode(node, length, width) {
    if (!node) return null;
    if (node.used) {
      return (
        this.findNode(node.right, length, width) ||
        this.findNode(node.down, length, width)
      );
    }
    return length <= node.length && width <= node.width ? node : null;
  }

  splitNode(node, length, width) {
    node.used = true;
    node.down = {
      x: node.x,
      y: node.y + width,
      length: node.length,
      width: node.width - width,
      used: false,
    };
    node.right = {
      x: node.x + length,
      y: node.y,
      length: node.length - length,
      width,
      used: false,
    };
    return { x: node.x, y: node.y };
  }
}

function numberOr(value, fallback) {
  const parsed = Number(value);
  return Number.isFinite(parsed) ? parsed : fallback;
}

function orientations(piece, rotationAllowed) {
  const normal = { length: piece.length, width: piece.width, rotated: false };
  if (!rotationAllowed || piece.length === piece.width) return [normal];
  return [normal, { length: piece.width, width: piece.length, rotated: true }];
}

function candidateFit(sheet, piece) {
  const choices = orientations(piece, sheet.rotationAllowed);
  let best = null;

  choices.forEach((orientation) => {
    if (
      orientation.length > sheet.availableLength ||
      orientation.width > sheet.availableWidth
    ) {
      return;
    }

    const packedLength = Math.min(orientation.length + sheet.kerf, sheet.availableLength);
    const packedWidth = Math.min(orientation.width + sheet.kerf, sheet.availableWidth);
    const trial = sheet.packer.clone();
    const fit = trial.fitOne(packedLength, packedWidth);
    if (!fit) return;

    const score =
      fit.y * sheet.availableLength +
      fit.x +
      (orientation.rotated ? 0.1 : 0) +
      (sheet.source === "rest" ? 0 : 100_000);

    if (!best || score < best.score) {
      best = { orientation, fit, trial, score };
    }
  });

  return best;
}

function createSheet({
  thickness,
  source,
  sheetConfig,
  restMeta = null,
}) {
  return {
    thickness,
    source,
    ...sheetConfig,
    restStockId: restMeta?.restStockId || null,
    restStockLabel: restMeta?.label || null,
    restStockOccurrence: restMeta?.occurrence || null,
    packer: new BinaryTreePacker(
      sheetConfig.availableLength + sheetConfig.kerf,
      sheetConfig.availableWidth + sheetConfig.kerf,
    ),
    placements: [],
  };
}

function packThicknessGroup(items, baseConfig, thickness, restStock = []) {
  const sheets = [];

  restStockForThickness(restStock, thickness).forEach((restPiece) => {
    sheets.push(createSheet({
      thickness,
      source: "rest",
      sheetConfig: buildSheetConfig(
        restPiece.length,
        restPiece.width,
        baseConfig.margin,
        baseConfig.kerf,
        baseConfig.rotationAllowed,
      ),
      restMeta: restPiece,
    }));
  });

  items
    .sort(
      (a, b) =>
        Math.max(b.length, b.width) - Math.max(a.length, a.width) ||
        b.length * b.width - a.length * a.width ||
        b.width - a.width,
    )
    .forEach((item) => {
      let selectedSheet;
      let selectedFit;

      for (const sheet of sheets) {
        const fit = candidateFit(sheet, item);
        if (fit) {
          selectedSheet = sheet;
          selectedFit = fit;
          break;
        }
      }

      if (!selectedSheet) {
        const sheetConfig = buildSheetConfig(
          baseConfig.sheetLength,
          baseConfig.sheetWidth,
          baseConfig.margin,
          baseConfig.kerf,
          baseConfig.rotationAllowed,
        );
        selectedSheet = createSheet({
          thickness,
          source: "new",
          sheetConfig,
        });
        sheets.push(selectedSheet);
        selectedFit = candidateFit(selectedSheet, item);
      }

      if (!selectedFit) return;

      selectedSheet.packer = selectedFit.trial;
      selectedSheet.placements.push({
        panelId: item.id,
        projectId: item.projectId,
        projectName: item.projectName,
        projectColor: item.projectColor,
        panelIndex: item.panelIndex,
        name: item.name,
        occurrence: item.occurrence,
        thickness,
        x: selectedFit.fit.x + selectedSheet.margin,
        y: selectedFit.fit.y + selectedSheet.margin,
        length: selectedFit.orientation.length,
        width: selectedFit.orientation.width,
        originalLength: item.length,
        originalWidth: item.width,
        rotated: selectedFit.orientation.rotated,
      });
    });

  return sheets
    .filter((sheet) => sheet.placements.length > 0)
    .map((sheet, index) => ({
      ...sheet,
      thicknessSheetIndex: index,
    }));
}

function usedRestStockKeys(sheets) {
  const keys = new Set();
  sheets.forEach((sheet) => {
    if (sheet.source === "rest" && sheet.placements.length > 0 && sheet.restStockId) {
      keys.add(`${sheet.restStockId}:${sheet.restStockOccurrence}`);
    }
  });
  return keys;
}

function buildUnusedRestStock(restStock, groups, standardConfig, rotationAllowed, sheets) {
  const unused = [];
  const usedKeys = usedRestStockKeys(sheets);
  const normalizedRestStock = Array.isArray(restStock) ? restStock : [];

  normalizedRestStock.forEach((item) => {
    if (!isValidRestStockItem(item)) {
      if (item.length || item.width || item.quantity) {
        unused.push({
          restStockId: item.id,
          occurrence: 1,
          label: item.label,
          length: item.length,
          width: item.width,
          thickness: item.thickness,
          reason: "invalid",
          message: {
            key: "core.restUnused.invalid",
            params: { minSize: MIN_PANEL_SIZE },
          },
        });
      }
      return;
    }

    for (let occurrence = 1; occurrence <= item.quantity; occurrence += 1) {
      const key = `${item.id}:${occurrence}`;
      if (usedKeys.has(key)) continue;

      const label = item.label || {
        key: "core.restUnused.defaultLabel",
        params: { length: item.length, width: item.width },
      };
      const entry = {
        restStockId: item.id,
        occurrence,
        label,
        length: item.length,
        width: item.width,
        thickness: item.thickness,
      };

      if (!groups.has(item.thickness)) {
        unused.push({
          ...entry,
          reason: "wrong_thickness",
          message: {
            key: "core.restUnused.wrongThickness",
            params: { thickness: item.thickness },
          },
        });
        continue;
      }

      const groupItems = groups.get(item.thickness);
      const anyFits = groupItems.some((piece) => pieceFitsOnSheet(
        piece,
        rotationAllowed,
        item.length,
        item.width,
        standardConfig.margin,
        standardConfig.kerf,
      ));

      if (!anyFits) {
        unused.push({
          ...entry,
          reason: "too_small",
          message: {
            key: "core.restUnused.tooSmall",
            params: { length: item.length, width: item.width },
          },
        });
        continue;
      }

      unused.push({
        ...entry,
        reason: "not_needed",
        message: { key: "core.restUnused.notNeeded" },
      });
    }
  });

  return unused;
}

function pieceFitsOnSheet(piece, rotationAllowed, sheetLength, sheetWidth, margin, kerf) {
  const config = buildSheetConfig(sheetLength, sheetWidth, margin, kerf, rotationAllowed);
  return orientations(piece, rotationAllowed).some(
    (orientation) =>
      orientation.length <= config.availableLength &&
      orientation.width <= config.availableWidth,
  );
}

function pieceFitsSomewhere(item, rotationAllowed, standardConfig, restPieces) {
  const configs = [
    {
      availableLength: standardConfig.availableLength,
      availableWidth: standardConfig.availableWidth,
    },
    ...restPieces.map((restPiece) => {
      const config = buildSheetConfig(
        restPiece.length,
        restPiece.width,
        standardConfig.margin,
        standardConfig.kerf,
        rotationAllowed,
      );
      return {
        availableLength: config.availableLength,
        availableWidth: config.availableWidth,
      };
    }),
  ];

  return configs.some((config) => orientations(item, rotationAllowed).some(
    (orientation) =>
      orientation.length <= config.availableLength &&
      orientation.width <= config.availableWidth,
  ));
}

/**
 * Automatic multi-project, multi-thickness nesting.
 *
 * Pieces of different thicknesses are deliberately split into independent
 * nesting pools. Rest stock is tried before purchasing new full sheets.
 */
export function buildLayout(sourcePanel, projects, restStock = []) {
  const panelLength = Math.max(numberOr(sourcePanel.length, 2440), MIN_PANEL_SIZE);
  const panelWidth = Math.max(numberOr(sourcePanel.width, 1220), MIN_PANEL_SIZE);
  const defaultThickness = Math.max(numberOr(sourcePanel.defaultThickness, 18), 1);
  const margin = Math.max(numberOr(sourcePanel.margin, 0), 0);
  const kerf = Math.max(numberOr(sourcePanel.kerf, 0), 0);
  const rotationAllowed = Boolean(sourcePanel.allowRotation) && !Boolean(sourcePanel.grain);
  const standardConfig = buildSheetConfig(
    panelLength,
    panelWidth,
    margin,
    kerf,
    rotationAllowed,
  );
  const normalizedRestStock = Array.isArray(restStock) ? restStock : [];

  const groups = new Map();
  const invalid = [];
  let partCount = 0;

  projects.forEach((project) => {
    project.panels.forEach((panel, panelIndex) => {
      if (panel.hidden) return;

      const length = Math.max(numberOr(panel.length, 0), 0);
      const width = Math.max(numberOr(panel.width, 0), 0);
      const thickness = Math.max(numberOr(panel.thickness, defaultThickness), 1);
      const quantity = Math.max(Math.floor(numberOr(panel.quantity, 0)), 0);

      if (!length || !width || !quantity) return;

      const item = {
        id: panel.id,
        projectId: project.id,
        projectName: project.name || "Naamloos project",
        projectColor: project.color,
        panelIndex,
        length,
        width,
        thickness,
        name: panel.name || `Paneel ${panelIndex + 1}`,
      };

      const fits = pieceFitsSomewhere(
        item,
        rotationAllowed,
        standardConfig,
        restStockForThickness(normalizedRestStock, thickness),
      );

      if (!fits) {
        invalid.push({
          ...item,
          reason: `${length} × ${width} mm past niet op ${standardConfig.availableLength} × ${standardConfig.availableWidth} mm`,
        });
        return;
      }

      const items = groups.get(thickness) || [];
      for (let occurrence = 1; occurrence <= quantity; occurrence += 1) {
        items.push({ ...item, occurrence });
        partCount += 1;
      }
      groups.set(thickness, items);
    });
  });

  const sheets = [];
  const breakdown = [];

  [...groups.keys()]
    .sort((a, b) => b - a)
    .forEach((thickness) => {
      const thicknessSheets = packThicknessGroup(
        groups.get(thickness),
        standardConfig,
        thickness,
        normalizedRestStock,
      );
      const startIndex = sheets.length;
      const newSheetCount = thicknessSheets.filter((sheet) => sheet.source !== "rest").length;
      const restSheetCount = thicknessSheets.filter((sheet) => sheet.source === "rest").length;

      thicknessSheets.forEach((sheet, index) => {
        sheets.push({ ...sheet, index: startIndex + index });
      });

      breakdown.push({
        thickness,
        sheetCount: thicknessSheets.length,
        newSheetCount,
        restSheetCount,
        partCount: groups.get(thickness).length,
      });
    });

  const plateNumbersByPanelId = {};
  projects.forEach((project) => project.panels.forEach((panel) => {
    plateNumbersByPanelId[panel.id] = [];
  }));

  sheets.forEach((sheet) => {
    sheet.placements.forEach((placement) => {
      if (!plateNumbersByPanelId[placement.panelId].includes(sheet.index + 1)) {
        plateNumbersByPanelId[placement.panelId].push(sheet.index + 1);
      }
    });
  });

  const usedArea = sheets.reduce(
    (sum, sheet) =>
      sum +
      sheet.placements.reduce(
        (subtotal, placement) =>
          subtotal + placement.originalLength * placement.originalWidth,
        0,
      ),
    0,
  );
  const materialArea = sheets.reduce(
    (sum, sheet) => sum + sheet.sheetLength * sheet.sheetWidth,
    0,
  );
  const purchasedArea = sheets
    .filter((sheet) => sheet.source !== "rest")
    .reduce((sum, sheet) => sum + sheet.sheetLength * sheet.sheetWidth, 0);
  const newSheetCount = sheets.filter((sheet) => sheet.source !== "rest").length;
  const restSheetCount = sheets.filter((sheet) => sheet.source === "rest").length;
  const unusedRestStock = buildUnusedRestStock(
    normalizedRestStock,
    groups,
    standardConfig,
    rotationAllowed,
    sheets,
  );

  return {
    sheets,
    breakdown,
    invalid,
    unusedRestStock,
    plateNumbersByPanelId,
    metrics: {
      sheetCount: sheets.length,
      newSheetCount,
      restSheetCount,
      partCount,
      usedArea,
      wasteArea: Math.max(materialArea - usedArea, 0),
      purchasedArea,
      utilisation: materialArea ? (usedArea / materialArea) * 100 : 0,
    },
    panelLength,
    panelWidth,
    availableLength: standardConfig.availableLength,
    availableWidth: standardConfig.availableWidth,
  };
}
